/**
 * LoyaltyAdminSection — Testimonial Loyalty Scheme admin panel.
 *
 * Rendered inside the Super Admin → Coach Details dialog. Shows the coach's
 * current loyalty tier + years and lets a super admin backdate the loyalty
 * start date (used for early adopters, goodwill grants, corrections).
 *
 * Every save writes a `super_admin_audit_log` entry via the backend endpoint.
 */
import { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Award, Calendar, Save } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Human-friendly date input value: prefer the loyalty start, fall back to
// created_at, and finally today so the date picker isn't empty.
const toDateInput = (iso) => {
  if (!iso) return '';
  try {
    return new Date(iso).toISOString().slice(0, 10);
  } catch {
    return '';
  }
};

export function LoyaltyAdminSection({ coach, onUpdated }) {
  const loyalty = coach?.loyalty || {};
  const startForInput = toDateInput(coach?.loyalty_subscription_start || loyalty.start_date || coach?.created_at);

  const [editing, setEditing] = useState(false);
  const [dateValue, setDateValue] = useState(startForInput);
  const [reason, setReason] = useState('');
  const [saving, setSaving] = useState(false);

  const token = typeof window !== 'undefined' ? localStorage.getItem('token') : null;

  const save = async () => {
    if (!dateValue) {
      toast.error('Pick a start date first');
      return;
    }
    setSaving(true);
    try {
      const res = await axios.put(
        `${API}/super-admin/coaches/${coach.id}/loyalty`,
        { loyalty_subscription_start: dateValue, reason: reason.trim() || null },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(res.data?.message || 'Loyalty start date updated');
      onUpdated?.(res.data?.loyalty);
      setEditing(false);
      setReason('');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update loyalty start date');
    } finally {
      setSaving(false);
    }
  };

  const cancel = () => {
    setEditing(false);
    setDateValue(startForInput);
    setReason('');
  };

  const tierBadge = loyalty.title ? (
    <Badge className="bg-amber-100 text-amber-700 hover:bg-amber-100" data-testid="loyalty-tier-badge">
      {loyalty.title} · {loyalty.discount_pct}% off
    </Badge>
  ) : (
    <Badge variant="secondary" data-testid="loyalty-tier-badge">Not yet a Squad Player</Badge>
  );

  const nextLine = loyalty.at_peak
    ? 'Testimonial Coach — top tier reached. Discount locked forever.'
    : loyalty.next_tier_title
      ? `Next: ${loyalty.next_tier_title} (${loyalty.next_tier_discount}% off) in ${loyalty.days_to_next} day${loyalty.days_to_next === 1 ? '' : 's'}`
      : 'Loyalty clock starts on their next completed subscription cycle.';

  return (
    <div className="pt-4 border-t" data-testid="loyalty-admin-section">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <Award className="w-4 h-4 text-amber-500" />
          <Label className="text-gray-700 font-semibold">Testimonial Loyalty Scheme</Label>
        </div>
        {!editing && (
          <Button
            type="button"
            size="sm"
            variant="outline"
            onClick={() => setEditing(true)}
            data-testid="loyalty-edit-btn"
          >
            <Calendar className="w-3.5 h-3.5 mr-1" />
            Backdate start
          </Button>
        )}
      </div>

      <div className="grid grid-cols-3 gap-3 mb-2">
        <div className="text-center p-3 bg-amber-50 rounded-xl border border-amber-100">
          <p className="text-2xl font-bold text-amber-700" data-testid="loyalty-years">{loyalty.years ?? 0}</p>
          <p className="text-xs text-gray-500">Years</p>
        </div>
        <div className="text-center p-3 bg-amber-50 rounded-xl border border-amber-100">
          <p className="text-2xl font-bold text-amber-700" data-testid="loyalty-discount">{loyalty.discount_pct ?? 0}%</p>
          <p className="text-xs text-gray-500">Discount</p>
        </div>
        <div className="flex flex-col items-center justify-center p-3 bg-amber-50 rounded-xl border border-amber-100">
          {tierBadge}
          <p className="text-[10px] text-gray-500 mt-1">Current tier</p>
        </div>
      </div>

      <p className="text-xs text-gray-500 italic" data-testid="loyalty-next-line">{nextLine}</p>
      {loyalty.start_date && (
        <p className="text-[11px] text-gray-400 mt-1">
          Loyalty clock started {new Date(loyalty.start_date).toLocaleDateString()}
          {loyalty.start_date_source === 'created_at' && ' (defaulted from signup date)'}
        </p>
      )}

      {editing && (
        <div className="mt-3 p-3 rounded-lg border border-amber-200 bg-amber-50/60 space-y-3" data-testid="loyalty-edit-panel">
          <div>
            <Label className="text-xs">New loyalty start date</Label>
            <Input
              type="date"
              value={dateValue}
              max={new Date().toISOString().slice(0, 10)}
              onChange={(e) => setDateValue(e.target.value)}
              className="mt-1"
              data-testid="loyalty-date-input"
            />
          </div>
          <div>
            <Label className="text-xs">Reason (audit log note)</Label>
            <Input
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="e.g. Early adopter — backdated to launch date"
              className="mt-1"
              data-testid="loyalty-reason-input"
            />
          </div>
          <div className="flex items-center gap-2 justify-end">
            <Button type="button" size="sm" variant="ghost" onClick={cancel} disabled={saving} data-testid="loyalty-cancel-btn">
              Cancel
            </Button>
            <Button
              type="button"
              size="sm"
              onClick={save}
              disabled={saving}
              className="bg-amber-500 hover:bg-amber-600 text-white"
              data-testid="loyalty-save-btn"
            >
              <Save className="w-3.5 h-3.5 mr-1" />
              {saving ? 'Saving…' : 'Save'}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}

export default LoyaltyAdminSection;
